/* ============================================================
   EESHKHERA THEME — Main JS
   ============================================================ */

/* ---- Cart State ---- */
const Cart = {
  async get() {
    const res = await fetch('/cart.js');
    return res.json();
  },
  async add(variantId, quantity = 1, properties = {}) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity, properties })
    });
    if (!res.ok) throw new Error('Could not add to cart');
    return res.json();
  },
  async update(updates) {
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    });
    return res.json();
  },
  async change(line, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line, quantity })
    });
    return res.json();
  }
};

/* ---- Toast ---- */
function showToast(message, type = '') {
  let container = document.querySelector('.toast-container');
  if (!container) {
    container = document.createElement('div');
    container.className = 'toast-container';
    document.body.appendChild(container);
  }
  const toast = document.createElement('div');
  toast.className = `toast ${type}`;
  toast.textContent = message;
  container.appendChild(toast);
  setTimeout(() => toast.remove(), 3000);
}

/* ---- Theme Toggle ---- */
function initThemeToggle() {
  const btn = document.querySelector('[data-theme-toggle]');
  if (!btn) return;

  const STORAGE_KEY = 'eeshkhera-theme';
  const root = document.documentElement;

  const syncAria = () => {
    const isLight = root.getAttribute('data-theme') === 'light';
    btn.setAttribute('aria-pressed', String(isLight));
    btn.setAttribute('aria-label', isLight ? 'Switch to dark theme' : 'Switch to light theme');
  };

  syncAria();

  btn.addEventListener('click', () => {
    const next = root.getAttribute('data-theme') === 'light' ? 'dark' : 'light';
    if (next === 'dark') {
      root.removeAttribute('data-theme');
    } else {
      root.setAttribute('data-theme', 'light');
    }
    try { localStorage.setItem(STORAGE_KEY, next); } catch (e) {}
    syncAria();
  });
}

/* ---- Header: sticky shrink ---- */
function initHeader() {
  const header = document.querySelector('.site-header');
  if (!header) return;
  const syncHeader = () => {
    header.classList.toggle('scrolled', window.scrollY > 40);
  };
  syncHeader();
  window.addEventListener('scroll', () => {
    syncHeader();
  }, { passive: true });
}

/* ---- Announcement: black-first reveal on upward scroll ---- */
function initAnnouncementBar() {
  const bar = document.querySelector('.announcement-bar');
  if (!bar) return;

  const root = document.body;
  let lastY = window.scrollY;
  let ticking = false;
  const TOP_HIDE_Y = 24;
  const SHOW_AFTER_Y = 90;
  const DIR_DELTA = 5;

  root.classList.remove('announcement-visible');
  root.classList.add('announcement-hidden');

  const update = () => {
    const y = window.scrollY;
    const dy = y - lastY;

    if (y <= TOP_HIDE_Y) {
      root.classList.remove('announcement-visible');
      root.classList.add('announcement-hidden');
      lastY = y;
      ticking = false;
      return;
    }

    if (dy <= -DIR_DELTA && y > SHOW_AFTER_Y) {
      root.classList.add('announcement-visible');
      root.classList.remove('announcement-hidden');
    } else if (dy >= DIR_DELTA) {
      root.classList.remove('announcement-visible');
      root.classList.add('announcement-hidden');
    }

    lastY = y;
    ticking = false;
  };

  window.addEventListener('scroll', () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(update);
  }, { passive: true });
}

/* ---- Mobile Nav ---- */
function initMobileNav() {
  const menuBtn = document.querySelector('.header-menu-btn');
  const mobileNav = document.querySelector('.mobile-nav');
  const closeBtn = document.querySelector('.mobile-nav-close');
  if (!menuBtn || !mobileNav) return;

  menuBtn.addEventListener('click', () => {
    mobileNav.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    document.body.classList.add('menu-open');
  });
  const close = () => {
    mobileNav.classList.remove('is-open');
    document.body.style.overflow = '';
    document.body.classList.remove('menu-open');
  };
  if (closeBtn) closeBtn.addEventListener('click', close);
  mobileNav.addEventListener('click', (e) => { if (e.target === mobileNav) close(); });
}

/* ---- Cart Drawer ---- */
function initCartDrawer() {
  const overlay = document.querySelector('.cart-drawer-overlay');
  const drawer = document.querySelector('.cart-drawer');
  const closeBtn = document.querySelector('.cart-drawer-close');
  const cartBtns = document.querySelectorAll('[data-cart-toggle]');

  if (!drawer) return;

  const open = () => {
    overlay?.classList.add('is-open');
    drawer.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    document.body.classList.add('cart-open');
    refreshCartDrawer();
  };
  const close = () => {
    overlay?.classList.remove('is-open');
    drawer.classList.remove('is-open');
    document.body.style.overflow = '';
    document.body.classList.remove('cart-open');
  };

  cartBtns.forEach(btn => btn.addEventListener('click', open));
  closeBtn?.addEventListener('click', close);
  overlay?.addEventListener('click', close);

  document.addEventListener('cart:open', open);
  document.addEventListener('cart:close', close);
  document.addEventListener('cart:refresh', refreshCartDrawer);
}

async function refreshCartDrawer() {
  const cart = await Cart.get();
  updateCartCount(cart.item_count);
  renderCartItems(cart);
}

function updateCartCount(count) {
  document.querySelectorAll('.cart-count').forEach(el => {
    el.textContent = count;
    el.style.display = count > 0 ? 'flex' : 'none';
  });
}

function renderCartItems(cart) {
  const container = document.querySelector('.cart-drawer-items');
  const subtotalEl = document.querySelector('.cart-subtotal-value');
  if (!container) return;

  if (subtotalEl) {
    subtotalEl.textContent = formatMoney(cart.total_price);
  }

  if (cart.item_count === 0) {
    container.innerHTML = `
      <div class="cart-empty">
        <div class="cart-empty-icon">🛒</div>
        <h3>Your cart is empty</h3>
        <p>Add some heat to your cart</p>
        <a href="/collections/all" class="btn btn-primary" style="margin-top:8px">Shop Now</a>
      </div>`;
    return;
  }

  container.innerHTML = cart.items.map((item, idx) => `
    <div class="cart-item" data-line="${idx + 1}">
      <img class="cart-item-img" src="${item.image}" alt="${item.title}">
      <div class="cart-item-details">
        <div class="cart-item-brand">${item.vendor}</div>
        <div class="cart-item-title">${item.product_title}</div>
        ${item.variant_title && item.variant_title !== 'Default Title' ? `<div class="cart-item-variant">${item.variant_title}</div>` : ''}
        <div class="cart-item-price">${formatMoney(item.final_line_price)}</div>
        <div class="cart-item-controls">
          <div class="cart-item-qty">
            <button data-qty-change="-1" data-line="${idx + 1}">−</button>
            <span>${item.quantity}</span>
            <button data-qty-change="1" data-line="${idx + 1}">+</button>
          </div>
          <button class="cart-item-remove" data-line="${idx + 1}">Remove</button>
        </div>
      </div>
    </div>`).join('');

  container.querySelectorAll('[data-qty-change]').forEach(btn => {
    btn.addEventListener('click', async () => {
      const line = parseInt(btn.dataset.line);
      const change = parseInt(btn.dataset.qtyChange);
      const currentQty = cart.items[line - 1]?.quantity || 0;
      const newQty = Math.max(0, currentQty + change);
      await Cart.change(line, newQty);
      refreshCartDrawer();
    });
  });

  container.querySelectorAll('.cart-item-remove').forEach(btn => {
    btn.addEventListener('click', async () => {
      const line = parseInt(btn.dataset.line);
      await Cart.change(line, 0);
      refreshCartDrawer();
    });
  });
}

function formatMoney(cents) {
  return (cents / 100).toLocaleString('en-IN', { style: 'currency', currency: window.Shopify?.currency?.active || 'INR', maximumFractionDigits: 0 });
}

/* ---- Add to Cart (PDP) ---- */
function initAddToCart() {
  const form = document.querySelector('.product-add-form-el');
  if (!form) return;

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const btn = form.querySelector('.btn-add-to-cart');
    const variantId = form.querySelector('[name="id"]')?.value;
    const qty = parseInt(form.querySelector('[name="quantity"]')?.value || 1);
    if (!variantId) return;

    setButtonLoading(btn);
    try {
      await Cart.add(variantId, qty);
      await refreshCartDrawer();
      showToast('Added to cart!', 'success');
      FBEvents.emit('fb:cart-added');
      document.dispatchEvent(new Event('cart:open'));
    } catch {
      showToast('Out of stock', 'error');
    } finally {
      clearButtonLoading(btn);
    }
  });
}

/* ---- Quick Add ---- */
document.addEventListener('click', async (e) => {
  const btn = e.target.closest('.product-card-quick-add');
  if (!btn) return;
  const variantId = btn.dataset.variantId;
  if (!variantId) return;
  setButtonLoading(btn);
  try {
    await Cart.add(variantId, 1);
    await refreshCartDrawer();
    showToast('Added to cart!', 'success');
    FBEvents.emit('fb:cart-added');
    document.dispatchEvent(new Event('cart:open'));
  } catch {
    showToast('Out of stock', 'error');
  } finally {
    clearButtonLoading(btn);
  }
});

/* ---- Variant Selector (PDP) ---- */
function initVariantSelector() {
  const variantBtns = document.querySelectorAll('.variant-btn');
  if (!variantBtns.length) return;

  const variantsData = JSON.parse(document.querySelector('[data-variants]')?.textContent || '[]');

  variantBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      const option = btn.dataset.option;
      const value = btn.dataset.value;
      btn.closest('.variant-options').querySelectorAll('.variant-btn').forEach(b => b.classList.remove('selected'));
      btn.classList.add('selected');
      updateSelectedVariant(variantsData);
    });
  });
}

function updateSelectedVariant(variants) {
  const selected = {};
  document.querySelectorAll('.variant-btn.selected').forEach(btn => {
    selected[btn.dataset.option] = btn.dataset.value;
  });
  const match = variants.find(v =>
    v.options.every((opt, i) => opt === selected[`option${i+1}`])
  );
  if (match) {
    const idInput = document.querySelector('[name="id"]');
    if (idInput) idInput.value = match.id;
    const addBtn = document.querySelector('.btn-add-to-cart');
    if (addBtn) {
      addBtn.disabled = !match.available;
      addBtn.textContent = match.available ? 'Add to Cart' : 'Sold Out';
    }
    const priceEl = document.querySelector('.product-price-main');
    if (priceEl) priceEl.textContent = formatMoney(match.price);
  }
}

/* ---- Product Gallery ---- */
function initProductGallery() {
  const thumbnails = document.querySelectorAll('.product-thumbnail');
  const mainImg = document.querySelector('.product-main-img img');
  if (!thumbnails.length || !mainImg) return;

  thumbnails.forEach(thumb => {
    thumb.addEventListener('click', () => {
      thumbnails.forEach(t => t.classList.remove('active'));
      thumb.classList.add('active');
      mainImg.src = thumb.dataset.imageUrl || thumb.querySelector('img').src;
      mainImg.srcset = '';
    });
  });
}

/* ---- Quantity selector ---- */
function initQtySelector() {
  document.querySelectorAll('.qty-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const input = btn.closest('.qty-selector').querySelector('.qty-input');
      if (!input) return;
      const current = parseInt(input.value) || 1;
      const action = btn.dataset.action;
      input.value = action === 'plus' ? current + 1 : Math.max(1, current - 1);
    });
  });
}

/* ---- Marquee pause on hover ---- */
function initMarquee() {
  document.querySelectorAll('.marquee-track').forEach(track => {
    if (track.dataset.ready === 'true') return;
    track.dataset.ready = 'true';
  });
}

/* ---- Hero slideshow (desktop + mobile swipe) ---- */
function initHeroSlideshow() {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const body = document.body;

  document.querySelectorAll('.hero-slideshow').forEach(slideshow => {
    const slides = Array.from(slideshow.querySelectorAll('.hero-slide'));
    const dots = Array.from(slideshow.querySelectorAll('.hero-dot'));
    const progress = slideshow.querySelector('.hero-slide-progress-bar');
    const heroSection = slideshow.closest('.hero-section');
    if (!slides.length) return;

    const intervalSec = Math.max(2, parseInt(slideshow.dataset.interval || '3', 10) || 3);
    const intervalMs = intervalSec * 1000;

    let current = 0;
    let timer = null;
    let inView = true;
    let isHovered = false;
    let touchStartX = 0;
    let touchStartY = 0;

    const resetProgress = () => {
      if (!progress) return;
      progress.style.transition = 'none';
      progress.style.transform = 'scaleX(0)';
      void progress.offsetWidth;
      if (prefersReducedMotion) return;
      progress.style.transition = 'transform ' + intervalMs + 'ms linear';
      progress.style.transform = 'scaleX(1)';
    };

    const markHeroTransition = () => {
      if (!heroSection) return;
      heroSection.classList.remove('hero-slide-change');
      void heroSection.offsetWidth;
      heroSection.classList.add('hero-slide-change');
    };

    const goTo = (idx, reason = 'auto') => {
      if (slides.length < 2) return;
      slides[current].classList.remove('active');
      dots[current]?.classList.remove('active');

      current = (idx + slides.length) % slides.length;

      slides[current].classList.add('active');
      dots[current]?.classList.add('active');
      slideshow.style.setProperty('--hero-active-index', String(current));
      markHeroTransition();
      resetProgress();
      window.FBEvents?.emit('fb:hero-slide', { index: current, reason });
    };

    const stop = () => {
      if (!timer) return;
      clearInterval(timer);
      timer = null;
      if (progress) {
        progress.style.transition = 'none';
      }
    };

    const start = () => {
      if (timer || slides.length < 2 || !inView || isHovered || prefersReducedMotion) return;
      resetProgress();
      timer = setInterval(() => {
        goTo(current + 1, 'auto');
      }, intervalMs);
    };

    dots.forEach((dot, idx) => {
      dot.addEventListener('click', () => {
        stop();
        goTo(idx, 'dot');
        start();
      });
    });

    slideshow.addEventListener('mouseenter', () => {
      isHovered = true;
      stop();
    });
    slideshow.addEventListener('mouseleave', () => {
      isHovered = false;
      start();
    });

    slideshow.addEventListener('touchstart', (e) => {
      const touch = e.changedTouches?.[0];
      if (!touch) return;
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
      stop();
    }, { passive: true });

    slideshow.addEventListener('touchend', (e) => {
      const touch = e.changedTouches?.[0];
      if (!touch) {
        start();
        return;
      }
      const dx = touch.clientX - touchStartX;
      const dy = touch.clientY - touchStartY;
      if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) {
        goTo(current + (dx < 0 ? 1 : -1), 'swipe');
      }
      start();
    }, { passive: true });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stop();
      else start();
    });

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        const entry = entries[0];
        inView = !!entry && entry.isIntersecting && entry.intersectionRatio > 0.3;
        if (heroSection) {
          body.classList.toggle('fb-hero-inview', inView);
        }
        if (inView) start();
        else stop();
      }, { threshold: [0, 0.3, 0.6] });
      observer.observe(heroSection || slideshow);
    }

    goTo(0, 'init');
    if (!prefersReducedMotion) start();
  });
}

/* ---- Collection filter tags ---- */
function initFilterTags() {
  document.querySelectorAll('.filter-tag').forEach(tag => {
    tag.addEventListener('click', () => {
      document.querySelectorAll('.filter-tag').forEach(t => t.classList.remove('active'));
      tag.classList.add('active');
    });
  });
}

/* ---- Newsletter form ---- */
function initNewsletter() {
  document.querySelectorAll('.newsletter-form').forEach(form => {
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const input = form.querySelector('.newsletter-input');
      if (input?.value) {
        showToast('Subscribed! Welcome to the drop.', 'success');
        input.value = '';
      }
    });
  });
}

/* ============================================================
   FOOTBALL IDENTITY SYSTEM v2
   ============================================================ */

window.FBEvents = {
  _l: {},
  emit(e, d) { (this._l[e] || []).forEach(fn => fn(d)); },
  on(e, fn)  { if (!this._l[e]) this._l[e] = []; this._l[e].push(fn); }
};

const _fbReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const _fbMobile        = window.matchMedia('(pointer: coarse)').matches;

/* ---- Football button loading helpers ---- */
function setButtonLoading(btn) {
  if (!btn) return;
  btn.dataset.fbOriginalText = btn.textContent.trim();
  btn.classList.add('fb-loading');
  btn.disabled = true;
}
function clearButtonLoading(btn) {
  if (!btn) return;
  btn.classList.remove('fb-loading');
  btn.disabled = false;
  if (btn.dataset.fbOriginalText) {
    btn.textContent = btn.dataset.fbOriginalText;
    delete btn.dataset.fbOriginalText;
  }
}

/* ---- Module 1: Progress Bar ---- */
function initProgressBar() {
  if (_fbReducedMotion) return;
  const wrap = document.getElementById('fb-progress');
  const bar  = document.getElementById('fb-progress-bar');
  const ball = document.getElementById('fb-progress-ball');
  if (!wrap || !bar) return;
  const ballSvg = ball ? ball.querySelector('svg') : null;

  let safetyTimer;
  let spinRaf;
  let spinAngle = 0;
  let active = false;
  let fading = false;

  const resetBar = () => {
    bar.style.transition = 'none';
    bar.style.width = '0%';
    if (ball) {
      ball.style.transition = 'none';
      ball.style.left = '0%';
    }
    if (ballSvg) {
      ballSvg.style.transform = '';
    }
  };

  const spinBall = () => {
    if (!active || !ballSvg) return;
    spinAngle += 3.8;
    ballSvg.style.transform = 'rotate(' + spinAngle + 'deg)';
    spinRaf = requestAnimationFrame(spinBall);
  };

  const showBar = () => {
    if (active) return;
    active = true;
    fading = false;
    clearTimeout(safetyTimer);
    wrap.classList.remove('is-fading');
    wrap.style.opacity = '1';
    resetBar();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        bar.style.transition = 'width 0.9s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
        bar.style.width = '88%';
        if (ball) {
          ball.style.transition = 'left 0.9s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
          ball.style.left = '88%';
        }
      });
    });

    cancelAnimationFrame(spinRaf);
    spinRaf = requestAnimationFrame(spinBall);
    safetyTimer = setTimeout(hideBar, 8000);
  };

  const hideBar = () => {
    if (!active || fading) return;
    fading = true;
    bar.style.transition = 'width 220ms ease-out';
    bar.style.width = '100%';
    if (ball) {
      ball.style.transition = 'left 220ms ease-out';
      ball.style.left = '100%';
    }

    setTimeout(() => {
      wrap.classList.add('is-fading');
      wrap.style.opacity = '0';

      setTimeout(() => {
        active = false;
        fading = false;
        clearTimeout(safetyTimer);
        cancelAnimationFrame(spinRaf);
        wrap.classList.remove('is-fading');
        resetBar();
      }, 280);
    }, 160);
  };

  document.addEventListener('click', (e) => {
    const link = e.target.closest('a');
    if (!link) return;
    const href = link.getAttribute('href');
    if (!href || href.startsWith('#') || href.startsWith('javascript:') ||
        href.includes('/cart/') || href.includes('/cart.js') ||
        link.target === '_blank' ||
        (link.hostname && link.hostname !== window.location.hostname) ||
        e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
    showBar();
  });

  window.addEventListener('pageshow', () => {
    hideBar();
  });
}

/* ---- Module 2: Mascot Ball ---- */
function initFloatingFootball() {
  if (_fbReducedMotion) return;
  const ball   = document.getElementById('fb-float');
  const shadow = document.getElementById('fb-float-shadow');
  if (!ball) return;
  const ballSvg = ball.querySelector('svg');
  const desktopRoam = !_fbMobile;

  const cfg = _fbMobile
    ? { gravity: 0.23, bounce: 0.68, friction: 0.992, dockY: 92, dockRight: 12, floorGap: 18, kickY: 9.4, kickX: 8.2 }
    : { gravity: 0.31, bounce: 0.74, friction: 0.989, dockY: 88, dockRight: 18, floorGap: 24, kickY: 12.2, kickX: 13.8 };

  let state = 'docked';
  let posX = 0;
  let posY = 0;
  let velX = 0;
  let velY = 0;
  let angleRad = 0;
  let angVel = 0;
  let scaleX = 1;
  let scaleY = 1;
  let rafId = null;
  let scrollDebounce = null;
  let lastScrollY = window.scrollY;
  let activeFrames = 0;
  let motionSource = 'user';
  let suppressClickUntil = 0;
  let streak = 0;
  let lastSkillAt = 0;

  let dragging = false;
  let dragPointerId = null;
  let dragOffsetX = 0;
  let dragOffsetY = 0;
  let dragSamples = [];

  const goalTargets = Array.from(document.querySelectorAll('[data-fb-goal-target]'));
  const goalHud = document.querySelector('.fb-goal-hud');
  const goalCountEls = Array.from(document.querySelectorAll('[data-fb-goal-count]'));
  let goalCount = 0;
  let lastGoalAt = 0;
  let goalModeTimer = null;

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
  const getBallSize = () => ball.offsetWidth || (_fbMobile ? 68 : 84);

  const isUiBlocked = () => {
    const body = document.body;
    return document.hidden || body.classList.contains('cart-open') || body.classList.contains('menu-open');
  };

  const getBounds = () => {
    const size = getBallSize();
    return {
      size,
      left: 8,
      right: window.innerWidth - size - 8,
      ceiling: 56,
      floor: window.innerHeight - size - cfg.floorGap
    };
  };

  const getDock = () => {
    const size = getBallSize();
    return {
      x: Math.max(8, window.innerWidth - size - cfg.dockRight),
      y: cfg.dockY
    };
  };

  const showSkillText = (text, x, y, hot = false) => {
    const el = document.createElement('div');
    el.className = hot ? 'fb-trick-label is-hot' : 'fb-trick-label';
    el.textContent = text;
    el.style.left = x + 'px';
    el.style.top = y + 'px';
    document.body.appendChild(el);
    el.addEventListener('animationend', () => el.remove(), { once: true });
  };

  const updateGoalCount = () => {
    goalCountEls.forEach((el) => {
      el.textContent = String(goalCount);
    });
  };

  const flashGoalHud = () => {
    if (!goalHud) return;
    goalHud.classList.remove('is-flash');
    void goalHud.offsetWidth;
    goalHud.classList.add('is-flash');
  };

  const triggerGoalMode = (nutmeg) => {
    const body = document.body;
    body.classList.add('fb-goal-mode');
    body.classList.toggle('fb-goal-nutmeg', !!nutmeg);
    clearTimeout(goalModeTimer);
    goalModeTimer = setTimeout(() => {
      body.classList.remove('fb-goal-mode', 'fb-goal-nutmeg');
    }, 1200);
  };

  const detectGoalTarget = () => {
    if (!goalTargets.length) return null;
    const size = getBallSize();
    const cx = posX + size / 2;
    const cy = posY + size / 2;
    for (let i = 0; i < goalTargets.length; i += 1) {
      const target = goalTargets[i];
      if (!target) continue;
      const style = window.getComputedStyle(target);
      if (style.display === 'none' || style.visibility === 'hidden') continue;
      const mouth = target.querySelector('.fb-goal-mouth') || target;
      const rect = mouth.getBoundingClientRect();
      if (rect.width < 16 || rect.height < 16) continue;
      const inset = Math.max(6, size * 0.16);
      if (
        cx >= rect.left + inset &&
        cx <= rect.right - inset &&
        cy >= rect.top + inset &&
        cy <= rect.bottom - inset
      ) {
        return target;
      }
    }
    return null;
  };

  const registerGoal = (target) => {
    const now = Date.now();
    if (now - lastGoalAt < 760) return;
    lastGoalAt = now;

    const speed = Math.hypot(velX, velY);
    const nutmeg = motionSource === 'drag' && speed > 10.5 && Math.abs(velX) > Math.max(6.8, Math.abs(velY) * 1.15);
    const points = nutmeg ? 2 : 1;

    goalCount += points;
    updateGoalCount();
    flashGoalHud();
    triggerGoalMode(nutmeg);

    target.classList.remove('is-scored', 'is-nutmeg');
    void target.offsetWidth;
    target.classList.add('is-scored');
    if (nutmeg) {
      target.classList.add('is-nutmeg');
      showSkillText('NUTMEG GOAL!', posX + getBallSize() * 0.16, posY - 14, true);
    } else {
      showSkillText('GOAL!', posX + getBallSize() * 0.3, posY - 14, true);
    }
    setTimeout(() => target.classList.remove('is-scored', 'is-nutmeg'), 920);

    velY = -(Math.abs(velY) * 0.35 + 4.8);
    velX = velX * 0.62 + (Math.random() - 0.5) * 1.4;
    angVel += velX * 0.06;
    scaleX = 0.84;
    scaleY = 1.16;
    activeFrames = 0;

    window.FBEvents?.emit('fb:goal-scored', { points, nutmeg, score: goalCount });
  };

  const registerSkill = (source) => {
    if (source === 'roam') return;
    const now = Date.now();
    streak = (now - lastSkillAt < 2200) ? (streak + 1) : 1;
    lastSkillAt = now;

    if (streak === 10) {
      showSkillText('Padhai kyun nahin ho rahe?', posX + getBallSize() * 0.16, posY - 12, true);
    } else if (streak === 15) {
      showSkillText('Controller chhod, checkout khol.', posX + getBallSize() * 0.08, posY - 12, true);
    } else if (streak >= 4) {
      showSkillText(streak + 'x COMBO', posX + getBallSize() * 0.25, posY - 10, streak >= 6);
    }
  };

  const wallBanterThreshold = _fbMobile ? 3.8 : 6.5;

  const applyTransform = () => {
    ball.style.left = posX + 'px';
    ball.style.top = posY + 'px';
    ball.style.right = 'auto';

    if (ballSvg) {
      ballSvg.style.transition = '';
      ballSvg.style.transform =
        'rotate(' + angleRad.toFixed(3) + 'rad)' +
        ' scaleX(' + scaleX.toFixed(3) + ')' +
        ' scaleY(' + scaleY.toFixed(3) + ')';
    }

    if (shadow) {
      const { floor, size } = getBounds();
      const ratio = Math.min(1, Math.max(0, posY / Math.max(1, floor)));
      shadow.style.left = (posX + size * 0.12) + 'px';
      shadow.style.top = (window.innerHeight - 12) + 'px';
      shadow.style.transform = 'scaleX(' + (0.2 + ratio * 1.0).toFixed(3) + ') scaleY(' + (ratio * 0.68).toFixed(3) + ')';
      shadow.style.opacity = (ratio * 0.74).toFixed(2);
    }
  };

  const enterIdleAtCurrent = () => {
    state = 'docked';
    velX = 0;
    velY = 0;
    angVel = 0;
    scaleX = 1;
    scaleY = 1;
    ball.classList.remove('fb-active', 'fb-hidden', 'fb-dragging');
    ball.classList.add('fb-docked');
    if (ballSvg) {
      ballSvg.style.transition = '';
      ballSvg.style.transform = '';
    }
    if (shadow) {
      shadow.style.opacity = '0';
    }
    applyTransform();
  };

  const enterDocked = () => {
    cancelAnimationFrame(rafId);
    rafId = null;
    const dock = getDock();
    posX = dock.x;
    posY = dock.y;
    angleRad = 0;
    enterIdleAtCurrent();
  };

  const physicsLoop = () => {
    if (state !== 'active') return;

    if (desktopRoam && isUiBlocked()) {
      enterIdleAtCurrent();
      return;
    }

    const { left, right, ceiling, floor } = getBounds();

    activeFrames += 1;

    velY += cfg.gravity;
    velX *= cfg.friction;
    posX += velX;
    posY += velY;

    angleRad += angVel;
    angVel *= 0.94;

    if (posY >= floor) {
      posY = floor;
      const hitSpeed = Math.abs(velY);
      velY = -hitSpeed * cfg.bounce;
      angVel += velX * 0.035;
      if (hitSpeed > 2.4) {
        const sq = Math.min(0.34, hitSpeed * 0.03);
        scaleX = 1 + sq * 1.8;
        scaleY = 1 - sq;
      }
      if (Math.abs(velY) < 0.8) velY = 0;
    }

    if (posY < ceiling) {
      posY = ceiling;
      velY = Math.abs(velY) * 0.55;
    }

    if (posX <= left) {
      posX = left;
      velX = Math.abs(velX) * cfg.bounce;
      angVel *= -0.95;
      if (Math.abs(velX) > wallBanterThreshold && (motionSource === 'drag' || _fbMobile)) {
        showSkillText('Ho gaya?', posX + 10, posY + 4);
      }
    }
    if (posX >= right) {
      posX = right;
      velX = -Math.abs(velX) * cfg.bounce;
      angVel *= -0.95;
      if (Math.abs(velX) > wallBanterThreshold && (motionSource === 'drag' || _fbMobile)) {
        showSkillText('Ab merch le le.', posX - 22, posY + 4);
      }
    }

    scaleX += (1 - scaleX) * 0.18;
    scaleY += (1 - scaleY) * 0.18;
    applyTransform();

    const goalTarget = detectGoalTarget();
    if (goalTarget) {
      registerGoal(goalTarget);
    }

    const settled = posY >= floor - 1 && Math.abs(velY) < 0.72 && Math.abs(velX) < 0.48;
    if (activeFrames > 24 && settled) {
      enterIdleAtCurrent();
      return;
    }

    rafId = requestAnimationFrame(physicsLoop);
  };

  const kickBall = (clientX, intensity = 1, options = {}) => {
    const source = options.source || 'user';
    const forceDir = options.forceDir || 0;
    const extraLift = options.extraLift || 0;
    const rect = ball.getBoundingClientRect();
    const relX = ((clientX - rect.left) / Math.max(1, rect.width)) - 0.5;

    motionSource = source;

    posX = rect.left;
    posY = rect.top;
    velY = -(cfg.kickY * intensity + Math.random() * 1.3 + extraLift);
    velX = relX * (cfg.kickX * intensity)
      + (Math.random() - 0.5) * 1.8
      + forceDir * (cfg.kickX * 0.42 * intensity);
    angVel = velX * 0.13;
    scaleX = 1.2;
    scaleY = 0.76;
    activeFrames = 0;

    state = 'active';
    ball.classList.remove('fb-docked', 'fb-hero-pulse', 'fb-dragging');
    ball.classList.add('fb-active');

    registerSkill(source);

    cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(physicsLoop);
  };

  const finishDragThrow = (clientX, clientY) => {
    if (!dragging) return;
    dragging = false;
    dragPointerId = null;
    ball.classList.remove('fb-dragging');
    suppressClickUntil = Date.now() + 280;

    const first = dragSamples[0];
    const last = dragSamples[dragSamples.length - 1] || first;
    const dt = Math.max(16, (last?.t || 0) - (first?.t || 0));
    let throwVX = ((last?.x || clientX) - (first?.x || clientX)) / dt * 16;
    let throwVY = ((last?.y || clientY) - (first?.y || clientY)) / dt * 16;

    throwVX = clamp(throwVX, -20, 20);
    throwVY = clamp(throwVY, -20, 20);

    const speed = Math.hypot(throwVX, throwVY);
    if (speed < 2.2) {
      kickBall(clientX, 0.96, { source: 'user' });
      return;
    }

    motionSource = 'drag';
    state = 'active';
    velX = throwVX;
    velY = throwVY > -2 ? throwVY - 4 : throwVY;
    angVel = velX * 0.12;
    scaleX = 1.08;
    scaleY = 0.9;
    activeFrames = 0;

    registerSkill('drag');

    ball.classList.remove('fb-docked', 'fb-hero-pulse');
    ball.classList.add('fb-active');

    cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(physicsLoop);
  };

  if (desktopRoam) {
    ball.addEventListener('pointerdown', (e) => {
      if (e.button !== 0 || isUiBlocked()) return;
      e.preventDefault();
      const rect = ball.getBoundingClientRect();
      dragging = true;
      dragPointerId = e.pointerId;
      try { ball.setPointerCapture(dragPointerId); } catch {}
      dragOffsetX = e.clientX - rect.left;
      dragOffsetY = e.clientY - rect.top;
      dragSamples = [{ x: e.clientX, y: e.clientY, t: performance.now() }];
      posX = rect.left;
      posY = rect.top;
      velX = 0;
      velY = 0;
      state = 'dragging';
      motionSource = 'drag';
      ball.classList.remove('fb-docked', 'fb-hero-pulse');
      ball.classList.add('fb-active', 'fb-dragging');
      cancelAnimationFrame(rafId);
      applyTransform();
    });

    ball.addEventListener('pointermove', (e) => {
      if (!dragging || e.pointerId !== dragPointerId) return;
      const { left, right, ceiling, floor } = getBounds();
      posX = clamp(e.clientX - dragOffsetX, left, right);
      posY = clamp(e.clientY - dragOffsetY, ceiling, floor);
      angleRad += (e.movementX || 0) * 0.014;
      scaleX = 1.04;
      scaleY = 0.96;
      dragSamples.push({ x: e.clientX, y: e.clientY, t: performance.now() });
      if (dragSamples.length > 7) dragSamples.shift();
      applyTransform();
    });

    const endDrag = (e) => {
      if (!dragging || e.pointerId !== dragPointerId) return;
      try { ball.releasePointerCapture(dragPointerId); } catch {}
      finishDragThrow(e.clientX, e.clientY);
    };

    ball.addEventListener('pointerup', endDrag);
    ball.addEventListener('pointercancel', endDrag);
    ball.addEventListener('lostpointercapture', (e) => {
      if (!dragging || e.pointerId !== dragPointerId) return;
      finishDragThrow(posX + getBallSize() * 0.5, posY + getBallSize() * 0.5);
    });
  }

  if (!_fbMobile) {
    window.addEventListener('mousemove', (e) => {
      if (state !== 'docked' || !ballSvg || dragging) return;
      const rect = ball.getBoundingClientRect();
      const bx = rect.left + rect.width / 2;
      const by = rect.top + rect.height / 2;
      const dist = Math.hypot(e.clientX - bx, e.clientY - by);
      ballSvg.style.transition = 'transform 0.16s ease-out';
      if (dist < 180) {
        const a = Math.atan2(e.clientY - by, e.clientX - bx);
        const tilt = clamp(a * (180 / Math.PI) * 0.11, -11, 11);
        ballSvg.style.transform = 'rotate(' + tilt.toFixed(2) + 'deg)';
      } else {
        ballSvg.style.transform = '';
      }
    }, { passive: true });
  }

  window.addEventListener('scroll', () => {
    if (state !== 'docked' || !ballSvg || dragging) return;
    const dy = window.scrollY - lastScrollY;
    lastScrollY = window.scrollY;
    if (Math.abs(dy) < 3) return;
    ballSvg.style.setProperty('--fb-scroll-dir', (dy > 0 ? 8 : -8) + 'deg');
    ballSvg.classList.remove('fb-scroll-kick');
    void ballSvg.offsetWidth;
    ballSvg.classList.add('fb-scroll-kick');
    clearTimeout(scrollDebounce);
    scrollDebounce = setTimeout(() => {
      if (state === 'docked' && !dragging) {
        ballSvg.classList.remove('fb-scroll-kick');
        ballSvg.style.transform = '';
      }
    }, 420);
  }, { passive: true });

  ball.addEventListener('click', (e) => {
    if (Date.now() < suppressClickUntil || dragging) return;
    kickBall(e.clientX, _fbMobile ? 0.88 : 1, { source: 'user' });
  });

  ball.addEventListener('touchend', (e) => {
    e.preventDefault();
    const touch = e.changedTouches && e.changedTouches[0];
    if (!touch) return;
    kickBall(touch.clientX, _fbMobile ? 0.82 : 0.95, { source: 'user' });
  }, { passive: false });

  window.addEventListener('resize', () => {
    const { left, right, ceiling, floor } = getBounds();
    if (state === 'docked') {
      posX = clamp(posX, left, right);
      posY = clamp(posY, ceiling, floor);
      applyTransform();
      return;
    }
    posX = clamp(posX, left, right);
    posY = clamp(posY, ceiling, floor);
    applyTransform();
  }, { passive: true });

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      if (state === 'active') {
        enterIdleAtCurrent();
      }
      return;
    }
  });

  enterDocked();
  updateGoalCount();
}

/* ---- Module 3: Goal Celebration ---- */
function initGoalCelebration() {
  if (_fbReducedMotion) return;
  FBEvents.on('fb:cart-added', () => {
    const cartBtn = document.querySelector('[data-cart-toggle]');
    const rect = cartBtn ? cartBtn.getBoundingClientRect() : { left: window.innerWidth - 60, top: 20, width: 40, height: 40 };
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;

    const label = document.createElement('div');
    label.className = 'fb-goal-label';
    label.textContent = 'GOAL!';
    label.style.cssText = 'left:' + (cx - 30) + 'px;top:' + cy + 'px';
    document.body.appendChild(label);
    label.addEventListener('animationend', () => label.remove(), { once: true });

    const colors = ['var(--color-yellow)', '#fff', 'var(--color-yellow)', '#fff',
                    'var(--color-yellow)', '#fff', 'var(--color-yellow)', '#fff'];
    colors.forEach((color, i) => {
      const dot = document.createElement('div');
      dot.className = 'fb-confetti-dot';
      dot.style.background = color;
      dot.style.left = cx + 'px'; dot.style.top = cy + 'px';
      const angle = (i / colors.length) * 360 + (Math.random() - 0.5) * 30;
      const dist  = 36 + Math.random() * 22;
      const rad   = angle * Math.PI / 180;
      dot.style.setProperty('--fb-confetti-end',
        'translate(' + (Math.cos(rad) * dist).toFixed(1) + 'px,' + (Math.sin(rad) * dist).toFixed(1) + 'px) scale(0)');
      dot.style.animationDelay = (i * 18) + 'ms';
      document.body.appendChild(dot);
      dot.addEventListener('animationend', () => dot.remove(), { once: true });
    });

    const badge = document.querySelector('.cart-count');
    if (badge) {
      badge.classList.remove('fb-pulse');
      void badge.offsetWidth;
      badge.classList.add('fb-pulse');
      badge.addEventListener('animationend', () => badge.classList.remove('fb-pulse'), { once: true });
    }
  });
}

/* ---- Module 4: Scoreboard counter glow ---- */
function initScoreboardCounter() {
  const wrap = document.querySelector('.fb-score-wrap');
  if (!wrap) return;
  const observer = new MutationObserver(() => {
    wrap.style.boxShadow = '0 0 8px rgba(232,255,0,0.9)';
    setTimeout(() => { wrap.style.boxShadow = ''; }, 420);
  });
  observer.observe(wrap, { childList: true, subtree: true, characterData: true });
}

/* ---- Init ---- */
document.addEventListener('DOMContentLoaded', () => {
  initThemeToggle();
  initAnnouncementBar();
  initHeader();
  initMobileNav();
  initCartDrawer();
  initAddToCart();
  initVariantSelector();
  initProductGallery();
  initQtySelector();
  initMarquee();
  initHeroSlideshow();
  initFilterTags();
  initNewsletter();
  refreshCartDrawer();
  initProgressBar();
  initFloatingFootball();
  initGoalCelebration();
  initScoreboardCounter();
});
